import type {
	AnalyticsBreakdownOther,
	AnalyticsBreakdownResult,
	AnalyticsBreakdownRow,
	AnalyticsBreakdownStack,
	AnalyticsBreakdownStackKey
} from 'api-models'
import { ApiClient } from '../api-client'

// Client for the Analytics Report aggregation engine (DEV-2377). Mirrors the
// backend registries; ids are kept in sync with
// core/lib/services/advertiser/analytics/{dimension,metric}-registry.ts.

export type AnalyticsMetricId =
	| 'prevented_ad_waste'
	| 'blocked_sources'
	| 'bad_traffic_rate'
	| 'analyzed_ad_clicks'
	| 'valid_ad_clicks'
	| 'suspicious_ad_clicks'
	| 'bad_ad_clicks'
	| 'ctr'
	| 'cpc'
	| 'impressions'
	| 'engaged_clicks'
	| 'bounce_rate'
	| 'avg_page_views_per_visitor'
	| 'avg_clicks_per_visitor'
	| 'avg_clicks_to_convert'
	| 'avg_time_on_website'

export type AnalyticsDimensionId =
	| 'country'
	| 'region'
	| 'city'
	| 'ad_platform'
	| 'ad_account'
	| 'campaign_type'
	| 'campaign'
	| 'ad_group'
	| 'keyword'
	| 'isp'
	| 'ip_usage'
	| 'device_type'
	| 'os'
	| 'browser'
	| 'placement'
	| 'target_url'
	| 'status'
	| 'utm_source'
	| 'utm_campaign'
	| 'utm_content'
	| 'utm_medium'
	| 'utm_term'

export type MetricFormat =
	| 'integer'
	| 'currency0'
	| 'currency2'
	| 'percent1'
	| 'decimal1'
	| 'duration'

export interface AnalyticsScopeBody {
	dateFrom: string // ISO
	dateTo: string
	adPlatforms?: string[]
	adAccountIds?: string[]
	campaignIds?: string[]
}

export interface MetricCell {
	value: number | null // null → en-dash (unavailable for this dimension/row)
	prevValue?: number | null
}

export type MetricCards = Record<AnalyticsMetricId, MetricCell>

// The breakdown payload is shared with the backend + the frozen report snapshot
// (api-models), so the live page and the tokenised render route feed the same
// chart adapters (DEV-2432).
export type BreakdownRow = AnalyticsBreakdownRow
export type BreakdownResult = AnalyticsBreakdownResult
export type { AnalyticsBreakdownStack, AnalyticsBreakdownStackKey, AnalyticsBreakdownOther }

export interface AnalyticsTableRow {
	key: string
	label: string
	metrics: Partial<Record<AnalyticsMetricId, MetricCell>>
	hasChildren: boolean
	/** Leading-icon hint: ad-platform code (channel dims) or country ISO (region/city). */
	meta?: string | null
}
export interface AnalyticsTableResult {
	rows: AnalyticsTableRow[]
	total: number
	page: number
}

export interface DimensionMeta {
	id: AnalyticsDimensionId
	label: string
	available: boolean
}
export interface MetricMeta {
	id: AnalyticsMetricId
	label: string
	group: 'traffic_protection' | 'click_forensics' | 'performance' | 'behavior'
	format: MetricFormat
	additive: boolean
	chartMetric: boolean
	tableColumn: boolean
}
export interface AnalyticsMeta {
	dimensions: DimensionMeta[]
	metrics: MetricMeta[]
	chartMetrics: AnalyticsMetricId[]
	tableColumns: AnalyticsMetricId[]
}

export interface ParentFilter {
	dimension: AnalyticsDimensionId
	value: string
}

const base = (accountId: string, assetId: string) =>
	`/accounts/${accountId}/assets/${assetId}/analytics-report`

const getDimensions = async (
	accountId: string,
	assetId: string
): Promise<AnalyticsMeta> => {
	const { data } = await ApiClient.client.get(`${base(accountId, assetId)}/dimensions`)
	return data.data
}

const getMetrics = async (
	accountId: string,
	assetId: string,
	body: AnalyticsScopeBody
): Promise<MetricCards> => {
	const { data } = await ApiClient.client.post(`${base(accountId, assetId)}/metrics`, body)
	return data.data
}

const getBreakdown = async (
	accountId: string,
	assetId: string,
	body: AnalyticsScopeBody & {
		metric: AnalyticsMetricId
		dimension: AnalyticsDimensionId
		/** Stack-by dimension (Stacked bar). Must differ from `dimension`. */
		dimension2?: AnalyticsDimensionId
		topN?: number
	}
): Promise<BreakdownResult> => {
	const { data } = await ApiClient.client.post(`${base(accountId, assetId)}/breakdown`, body)
	return data.data
}

const getTable = async (
	accountId: string,
	assetId: string,
	body: AnalyticsScopeBody & {
		dimension: AnalyticsDimensionId
		parentPath?: ParentFilter[]
		filter?: 'all' | 'bad_rate_gt_10' | 'bad_rate_gt_25'
		sortMetric?: AnalyticsMetricId
		sortDir?: 'asc' | 'desc'
		search?: string
		page?: number
		pageSize?: number
	},
	// React Query's per-fetch AbortSignal. Passing it lets an unmount or a
	// superseding refetch actually cancel the request, so the query's
	// fetchStatus resets to idle instead of leaving an orphaned pending promise
	// that keeps isFetching (and the table's refetch overlay) stuck on forever.
	signal?: AbortSignal
): Promise<AnalyticsTableResult> => {
	const { data } = await ApiClient.client.post(`${base(accountId, assetId)}/table`, body, { signal })
	return data.data
}

// Fire-and-forget: bumps the downloads adoption counter after a client-side PDF
// export. Best-effort — never block or fail the download on this.
const trackDownload = (accountId: string, assetId: string): void => {
	void ApiClient.client
		.post(`${base(accountId, assetId)}/downloaded`, {})
		.catch(() => undefined)
}

export const AnalyticsReport = {
	getDimensions,
	getMetrics,
	getBreakdown,
	getTable,
	trackDownload
}
